import { mayCancelStop, openStopBranch } from '@folio/utils/stop-branch';
import { useCurrentFirmType } from '@folio/utils/useCurrentFirmType';
import { getBranchCloseReason, getBusinessRoleId } from '@folio/utils/stop-reasons';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  branchControllerInvalidateBranch,
  configControllerGetPublicProductConfigQueryOptions,
  legacyPermissionsControllerAtMeQueryOptions,
} from 'korint-sdk';

/** Closing the open stop branch is the cancellation: the backend drops its job and the BASE echo. */
export const useCancelStop = () => {
  const { policyOnMain: policy, branches, mutate } = usePolicyOnDashboard();
  const pending = !!policy.stop && policy.status !== 'POLICY_STOPPED';

  // Only cancelling needs an identity; nothing should block a dashboard that shows no stop.
  const { data: user } = useQuery({
    ...legacyPermissionsControllerAtMeQueryOptions(),
    enabled: pending,
  });

  const { data: allowedTo, isSuccess: isReasonConfigLoaded } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(policy.productId),
    enabled: pending,
    select: ({ policyStopReasons }) =>
      policyStopReasons?.find(({ reason }) => reason === policy.stop?.reason)?.allowedTo,
  });

  const stopBranch = openStopBranch(branches?.data);
  const { firmType } = useCurrentFirmType();

  // mutate, not mutateAsync: a rejected cancellation is already surfaced by the MutationCache.
  const { mutate: cancelStop, isPending: isCancelling } = useMutation({
    mutationFn: ({ branchId, comment }: { branchId: string; comment: string }) =>
      branchControllerInvalidateBranch(policy.id, branchId, {
        reason: getBranchCloseReason(user ?? {}),
        comment,
      }),
    onSuccess: () => mutate(),
  });

  return {
    // !user too: the close reason is attributed from it and the backend stores it verbatim.
    mayCancel:
      pending &&
      !!stopBranch &&
      !!user &&
      // Until the reason's roles are known, the fallback rule would offer a cancel it may refuse.
      (user.isSuperUser || firmType === 'WHOLESALE' || isReasonConfigLoaded) &&
      mayCancelStop({
        user,
        isWholesale: firmType === 'WHOLESALE',
        businessRoleId: getBusinessRoleId({ user, policy }),
        allowedTo,
        requestedByUserId: policy.stop?.requestedBy?.userId,
        policyBrokerageFirmId: policy.brokerageFirm,
      }),
    cancel: (comment: string, { onSuccess }: { onSuccess?: () => void } = {}) =>
      stopBranch && cancelStop({ branchId: stopBranch.id, comment }, { onSuccess }),
    isCancelling,
  };
};
